<script lang="ts">
import type { Map } from "maplibre-gl";
import type { PoiDensity } from "../../lib/geo/poi-density/index.ts";
import { STYLES } from "../../styles.ts";
import type { MapStyle } from "../../styles.ts";
import StyleDetailRow from "./StyleDetailRow.svelte";
import TilesetFreshnessControl from "./TilesetFreshnessControl.svelte";
import { Label } from "../ui/label/index.ts";
import * as RadioGroup from "../ui/radio-group/index.ts";
import { Separator } from "../ui/separator/index.ts";
import * as Sheet from "../ui/sheet/index.ts";

let {
  map,
  styleUrl,
  poiDensity,
  onstylechange,
  onpoidensitychange,
}: {
  map: Map;
  styleUrl: string;
  poiDensity: PoiDensity;
  onstylechange: (style: MapStyle) => void;
  onpoidensitychange: (density: PoiDensity) => void;
} = $props();

const defaultStyle = STYLES[0];
if (!defaultStyle) {
  throw new Error("Expected at least one map style");
}

const currentStyle = $derived(
  STYLES.find((s) => s.styleUrl === styleUrl) ?? defaultStyle,
);

function selectStyle(style: MapStyle): void {
  onstylechange(style);
}

const densityOptions: {
  value: PoiDensity;
  label: string;
  description: string;
}[] = [
  {
    value: "less",
    label: "Less",
    description: "Show POIs only after zooming in further.",
  },
  {
    value: "default",
    label: "Default",
    description: "Use the map style's original zoom levels.",
  },
  {
    value: "more",
    label: "More",
    description: "Show available POIs before zooming in as far.",
  },
];
</script>

<Sheet.Root>
  <Sheet.Trigger>
    {#snippet child({ props })}
      <button
        type="button"
        class="size-11 cursor-pointer overflow-hidden rounded-md border border-border bg-background shadow-sm transition hover:ring-2 hover:ring-ring/40 focus-visible:ring-3 focus-visible:ring-ring/50"
        aria-label="Map settings"
        {...props}
      >
        <img
          src={currentStyle.thumbnail}
          alt=""
          class="size-full object-cover"
        />
      </button>
    {/snippet}
  </Sheet.Trigger>

  <Sheet.Content
    side="left"
    class="gap-0 data-[state=open]:duration-250 data-[state=closed]:duration-150 data-[side=left]:w-[calc(100%-0.75rem)] data-[side=left]:max-w-none data-[side=left]:sm:w-96 data-[side=left]:sm:max-w-none"
  >
    <Sheet.Header class="shrink-0 border-b pr-12">
      <Sheet.Title>Map customization</Sheet.Title>
      <Sheet.Description>
        Customize the base map to your needs.
      </Sheet.Description>
    </Sheet.Header>

    <div class="min-h-0 flex-1 overflow-y-auto p-4">
      <section aria-labelledby="map-styles-heading">
        <h2 id="map-styles-heading" class="mb-3 text-sm font-medium">Styles</h2>
        <div class="grid gap-2">
          {#each STYLES as style}
            <button
              type="button"
              class="group flex w-full cursor-pointer items-center gap-3 rounded-lg border p-2 text-left transition-colors hover:bg-accent focus-visible:ring-3 focus-visible:ring-ring/50"
              class:border-foreground={style.styleUrl === styleUrl}
              class:bg-accent={style.styleUrl === styleUrl}
              aria-pressed={style.styleUrl === styleUrl}
              onclick={() => selectStyle(style)}
            >
              <span
                class="relative h-16 w-24 shrink-0 overflow-hidden rounded-md border bg-muted"
              >
                <img
                  src={style.thumbnail}
                  alt=""
                  class="size-full object-cover transition-transform group-hover:scale-110"
                />
              </span>
              <span class="min-w-0">
                <span class="block font-medium">{style.name}</span>
                <span class="block truncate text-xs">
                  by {style.creator}
                </span>
              </span>
            </button>
          {/each}
        </div>
      </section>

      <Separator class="my-5" />

      <fieldset>
        <legend class="mb-3 text-sm font-medium">
          POI density
        </legend>
        <RadioGroup.Root
          value={poiDensity}
          aria-label="POI density"
          onValueChange={(value) => onpoidensitychange(value as PoiDensity)}
        >
          {#each densityOptions as option}
            <div class="flex items-start gap-3">
              <RadioGroup.Item
                value={option.value}
                id={`poi-density-${option.value}`}
                class="mt-0.5"
              />
              <Label
                for={`poi-density-${option.value}`}
                class="grid cursor-pointer gap-1 leading-normal"
              >
                <span>{option.label}</span>
                <span class="font-normal text-muted-foreground">
                  {option.description}
                </span>
              </Label>
            </div>
          {/each}
        </RadioGroup.Root>
      </fieldset>

      <Separator class="my-5" />

      <section aria-labelledby="map-data-heading">
        <h2 id="map-data-heading" class="text-sm font-medium mb-4">
          About this style
        </h2>
        <div class="grid gap-2">
          <TilesetFreshnessControl {map} />
          <StyleDetailRow label="Creator">
            <a
              href={currentStyle.attributionURL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {currentStyle.creator}
            </a>
          </StyleDetailRow>
        </div>
      </section>
    </div>
  </Sheet.Content>
</Sheet.Root>
